import React, { useEffect, useCallback, useState, useRef } from 'react';
import { Edit, Trash2, Plus, Search, Building2, CheckCircle, XCircle, Eye } from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import OrganizationModal from './OrganizationModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import useOrganizationStore from '@/stores/SuperAdminStore/OrganizationStore';
import { LoadingSpinner } from '@/Components/common';
import './OrganizationListTab.css';

const OrganizationListTab: React.FC = () => {
  const navigate = useNavigate();
  const {
    organizations,
    organizationsPagination,
    searchTerm,
    selectedStatus,
    currentPage,
    itemsPerPage,
    loadingOrganizations,
    deleting,
    toggling,
    fetchOrganizations,
    createOrganization,
    updateOrganization,
    deleteOrganization,
    toggleStatus,
    setSearchTerm,
    setSelectedStatus,
    setCurrentPage,
    setItemsPerPage,
  } = useOrganizationStore();

  const [organizationToEdit, setOrganizationToEdit] = useState<{
    id?: number;
    name: string;
    isActive: boolean;
  } | null>(null);
  const [organizationToDelete, setOrganizationToDelete] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const hasFetchedRef = useRef(false);

  // Initial fetch on mount (prevent duplicate calls in React Strict Mode)
  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      fetchOrganizations({ page: currentPage, limit: itemsPerPage });
      setIsInitialMount(false);
    }
  }, []);

  // Debounced search (skip on initial mount)
  useEffect(() => {
    if (isInitialMount) return;

    const timer = setTimeout(() => {
      fetchOrganizations({ page: 1 });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, selectedStatus]);

  // Fetch on pagination change (skip on initial mount)
  useEffect(() => {
    if (isInitialMount) return;

    fetchOrganizations({ page: currentPage, limit: itemsPerPage });
  }, [currentPage, itemsPerPage]);

  const handleCreateOrganization = () => {
    setOrganizationToEdit({
      name: '',
      isActive: true,
    });
  };

  const handleEditOrganization = (organizationId: number) => {
    const org = organizations.find((o) => o.id === organizationId);
    if (org) {
      setOrganizationToEdit({
        id: org.id,
        name: org.name,
        isActive: org.isActive,
      });
    }
  };

  const handleDeleteOrganization = (organizationId: number) => {
    const org = organizations.find((o) => o.id === organizationId);
    if (org) {
      setOrganizationToDelete({
        id: org.id,
        name: org.name,
      });
    }
  };

  const handleCloseModal = () => {
    setOrganizationToEdit(null);
  };

  const handleCloseDeleteModal = () => {
    setOrganizationToDelete(null);
  };

  const handleSaveOrganization = async (data: { name: string; isActive: boolean }) => {
    let result;

    if (organizationToEdit?.id) {
      // Update existing organization
      result = await updateOrganization(organizationToEdit.id, {
        name: data.name,
        isActive: data.isActive,
      });
    } else {
      // Create new organization
      result = await createOrganization({
        name: data.name,
        isActive: data.isActive,
      });
    }

    if (result.success) {
      toast.success(result.message || 'Organization saved successfully');
      setOrganizationToEdit(null);
    } else {
      toast.error(result.message || 'Failed to save organization');
    }
  };

  const handleConfirmDelete = async () => {
    if (!organizationToDelete) return;

    const result = await deleteOrganization(organizationToDelete.id);

    if (result.success) {
      toast.success(result.message || 'Organization deleted successfully');
      setOrganizationToDelete(null);
    } else {
      toast.error(result.message || 'Failed to delete organization');
    }
  };

  const handleToggleStatus = async (organizationId: number, currentStatus: boolean) => {
    const result = await toggleStatus(organizationId, !currentStatus);

    if (result.success) {
      toast.success(result.message || 'Organization status updated successfully');
    } else {
      toast.error(result.message || 'Failed to update organization status');
    }
  };

  const handleViewOrganization = (organizationId: number) => {
    navigate(`/superadmin/organizations/${organizationId}`);
  };

  const totalPages = organizationsPagination?.totalPages || 1;
  const totalCount = organizationsPagination?.total || 0;

  // Format date
  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <div className="admin-organization-list">
      <div className="admin-organization-list-controls">
        <div className="admin-organization-list-search">
          <Search className="admin-organization-list-search-icon" size={18} />
          <input
            type="text"
            placeholder="Search organizations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <select
          className="admin-organization-list-select"
          value={selectedStatus.toString()}
          onChange={(e) => {
            const value = e.target.value;
            setSelectedStatus(value === '' ? '' : value === 'true');
            setCurrentPage(1);
          }}
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <button className="admin-organization-list-create-btn" onClick={handleCreateOrganization}>
          <Plus size={18} />
          Add Organization
        </button>
      </div>

      <div className="admin-organization-list-table-container">
        {loadingOrganizations ? (
          <div className="admin-organization-list-loading">
            <LoadingSpinner size="md" />
          </div>
        ) : organizations.length === 0 ? (
          <div className="admin-organization-list-empty">
            <Building2 size={48} color="#94a3b8" />
            <p>No organizations found</p>
            <button className="admin-organization-list-empty-btn" onClick={handleCreateOrganization}>
              <Plus size={18} />
              Create your first organization
            </button>
          </div>
        ) : (
          <table className="admin-organization-list-table">
            <thead>
              <tr>
                <th>Organization Name</th>
                <th>Status</th>
                <th>Users</th>
                <th>Created Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {organizations.map((org) => (
                <tr key={org.id}>
                  <td>
                    <div className="admin-organization-list-org-cell">
                      <div className="admin-organization-list-org-icon">
                        <Building2 size={20} />
                      </div>
                      <span className="admin-organization-list-org-name">{org.name}</span>
                    </div>
                  </td>
                  <td>
                    <button
                      className={`admin-organization-list-status-toggle ${org.isActive ? 'active' : 'inactive'}`}
                      onClick={() => handleToggleStatus(org.id, org.isActive)}
                      disabled={toggling}
                      title={org.isActive ? 'Click to deactivate' : 'Click to activate'}
                    >
                      {org.isActive ? (
                        <>
                          <CheckCircle size={16} />
                          <span>Active</span>
                        </>
                      ) : (
                        <>
                          <XCircle size={16} />
                          <span>Inactive</span>
                        </>
                      )}
                    </button>
                  </td>
                  <td>
                    <span className="admin-organization-list-user-count">
                      {org.userCount || 0} {org.userCount === 1 ? 'user' : 'users'}
                    </span>
                  </td>
                  <td>{formatDate(org.createdAt)}</td>
                  <td>
                    <div className="admin-organization-list-actions">
                      <button
                        className="admin-organization-list-action-btn view"
                        onClick={() => handleViewOrganization(org.id)}
                        title="View organization details"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        className="admin-organization-list-action-btn edit"
                        onClick={() => handleEditOrganization(org.id)}
                        title="Edit organization"
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        className="admin-organization-list-action-btn delete"
                        onClick={() => handleDeleteOrganization(org.id)}
                        title="Delete organization"
                        disabled={deleting}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {organizations.length > 0 && (
          <div className="admin-organization-list-pagination">
            <div className="admin-organization-list-pagination-info">
              <span>Showing</span>
              <select
                className="admin-organization-list-pagination-select"
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>of {totalCount}</span>
            </div>

            <div className="admin-organization-list-pagination-buttons">
              <button
                className="admin-organization-list-page-btn"
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum = i + 1;
                if (currentPage > 3 && totalPages > 5) {
                  pageNum = currentPage - 2 + i;
                  if (pageNum > totalPages) pageNum = totalPages - (4 - i);
                }
                if (pageNum < 1 || pageNum > totalPages) return null;
                return (
                  <button
                    key={pageNum}
                    className={`admin-organization-list-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                    onClick={() => setCurrentPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                className="admin-organization-list-page-btn"
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
            </div>
          </div>
        )}
      </div>

      {organizationToEdit && (
        <OrganizationModal
          organization={organizationToEdit}
          onClose={handleCloseModal}
          onSave={handleSaveOrganization}
        />
      )}

      {organizationToDelete && (
        <DeleteConfirmModal
          organizationName={organizationToDelete.name}
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          isDeleting={deleting}
        />
      )}
    </div>
  );
};

export default OrganizationListTab;
